import { useEffect, useRef, useState, type FormEvent } from 'react';
import { decodeJwt } from 'jose';
import { useApp } from './SessionProvider';
import { readLocal } from '../storage/local';
import { AsyncButton, Icon } from '../components/ui';

type GoogleIdentity = { accounts: { id: {
  initialize: (config: { client_id: string; callback: (response: { credential: string }) => void; auto_select: boolean }) => void;
  renderButton: (element: HTMLElement, options: { theme: string; size: string; text: string; shape?: string; width?: number; logo_alignment?: string }) => void;
  disableAutoSelect: () => void;
} } };
declare global { interface Window { google?: GoogleIdentity } }
export default function Login() {
  const { login, online } = useApp();
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(''); const [lastUser, setLastUser] = useState('');
  const [mode, setMode] = useState<'login' | 'register'>('login'); const [showPassword, setShowPassword] = useState(false);
  const [notice, setNotice] = useState('');
  const register = mode === 'register';
  const passwordUnavailable = (event?: FormEvent) => { event?.preventDefault(); setNotice('帳號密碼登入即將開放，目前請使用 Google 登入。'); };
  const switchMode = () => { setMode(register ? 'login' : 'register'); setNotice(''); };
  useEffect(() => {
    let active = true;
    void readLocal('lexispulse-test', 'last-account').then(value => { if (active && typeof value === 'string') setLastUser(value); });
    if (__MOCK__) return () => { active = false; };
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return () => { active = false; };
    const initialize = () => {
      if (!active || !window.google || !host.current) return;
      window.google.accounts.id.initialize({ client_id: clientId, auto_select: false, callback: response => {
        if (!active) return;
        const sub = decodeJwt(response.credential).sub;
        if (!sub) { setError('登入回應不合法'); return; }
        void login(sub, response.credential).catch(error => setError(error.message));
      } });
      window.google.accounts.id.renderButton(host.current, { theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', width: 336, logo_alignment: 'center' });
    };
    const script = document.createElement('script'); script.src = 'https://accounts.google.com/gsi/client';
    script.async = true; script.onload = initialize;
    script.onerror = () => { if (active) setError('Google 登入暫時無法載入，請檢查連線'); };
    if (window.google) initialize(); else document.head.appendChild(script);
    return () => { active = false; script.remove(); };
  }, [login]);
  const secretType = showPassword ? 'text' : 'password';
  return <main className="landing" data-mode={mode}>
    <section className="login-hero">
      <img className="login-mark" src="/brand/woriva-w.png" alt="" />
      <img className="login-word" src="/brand/woriva-wordmark.png" alt="Woriva" />
    </section>
    <section className="login-sheet" aria-labelledby="login-title">
      <div className="login-sheet-inner">
        <div className="login-heading">
          <h1 id="login-title">{register ? '建立帳號' : '歡迎回來'}</h1>
          <p>{register ? '開始收藏你的第一批單字' : '登入以繼續你的複習進度'}</p>
        </div>
        <form className="login-form" onSubmit={passwordUnavailable} noValidate>
          <label className="login-field"><span className="login-label">Email</span>
            <span className="login-input"><Icon name="envelope" /><input type="email" name="email" autoComplete="email" placeholder="you@example.com" /></span>
          </label>
          <label className="login-field"><span className="login-label">密碼
            {register ? null : <button type="button" className="login-link login-forgot" onClick={() => passwordUnavailable()}>忘記密碼？</button>}</span>
            <span className="login-input"><Icon name="lock" /><input type={secretType} name="password" autoComplete={register ? 'new-password' : 'current-password'} placeholder={register ? '至少 8 個字元' : '輸入密碼'} />
              <button type="button" className="login-reveal" aria-label={showPassword ? '隱藏密碼' : '顯示密碼'} onClick={() => setShowPassword(value => !value)}><Icon name={showPassword ? 'eye-slash' : 'eye'} /></button></span>
          </label>
          {register ? <label className="login-field"><span className="login-label">確認密碼</span>
            <span className="login-input"><Icon name="lock" /><input type={secretType} name="confirm-password" autoComplete="new-password" placeholder="再輸入一次密碼" /></span>
          </label> : null}
          <button type="submit" className="primary login-submit">{register ? '建立帳號' : '登入'}</button>
          {notice ? <p role="status" className="login-notice">{notice}</p> : null}
        </form>
        <div className="login-divider">或</div>
        <div ref={host} className="google-signin" />
        {__MOCK__ ? <div className="stack"><AsyncButton action={() => login('synthetic-user-a', 'synthetic-user-a')}>合成帳號 A</AsyncButton>
          <AsyncButton action={() => login('synthetic-user-b', 'synthetic-user-b')}>合成帳號 B</AsyncButton></div> : null}
        {!__MOCK__ && !import.meta.env.VITE_GOOGLE_CLIENT_ID ? <p role="alert" className="error">Google 登入尚未設定</p> : null}
        {!online && lastUser ? <AsyncButton action={() => login(lastUser, '')}>離線繼續</AsyncButton> : null}
        {error ? <p role="alert" className="error">{error}</p> : null}
        <p className="login-switch">{register ? '已經有帳號？' : '還沒有帳號？'}<button type="button" className="login-link" onClick={switchMode}>{register ? '登入' : '註冊'}</button></p>
      </div>
    </section>
  </main>;
}
